import { useState } from "react";
import { Table, UncontrolledTooltip } from "reactstrap";
import useDisplayMessage from "../../../common/lib/hooks/useDisplayMessage";
import KaAlert from "../../../common/lib/KaAlert";
import QueryView from "../../../common/lib/QueryView";
import OverflowTooltip from "../../../common/OverflowTooltip";
import Row from "../../../common/Row";
import SpinnerButton from "../../../common/SpinnerButton";
import { adminFacingErrorMessage } from "../../../common/userFacingMessages/userFacingMessages";
import useGetSiteSettings from "../../../requests/useGetSiteSettings";
import useUpdateBaySettings, { Bay, BayUpdate, DispenseType, Product, ProductUpdate } from "../../../requests/useUpdateBaySettings";
import { UpdateBayModal } from "./UpdateBayModal";
import { useUser } from "../../../userAccess/useUser";

export interface BaySettings {
  bays: Bay[];
  products: Product[];
}

enum ProductWarning {
  NotAvailable = 'NotAvailable',
  BulkInBothBay = 'BulkInBothBay',
  BlendInBulkBay = 'BlendInBulkBay',
  BlendInReceivingOnlyBay = 'BlendInReceivingOnlyBay',
}

const dispenseTypeDisplay = (bay: Bay) => {
  switch (bay.dispenseType) {
    case DispenseType.Blended:
      return "Blends";

    case DispenseType.Bulk:
      return "Bulk";

    case DispenseType.Both:
      return "Bulk & Blends";

    case DispenseType.None:
      return bay.bulkReceiving
        ? "Receiving Only"
        : "Disabled";
  }
}

const BayDirections = () => {
  const getBaySettings = useGetSiteSettings<BaySettings>();

  return <QueryView
    query={getBaySettings}
    renderData={settings =>
      !settings.bays.length
        ? <div className="alert alert-dismissible alert-danger" role="alert">
          Current site does not have any bays
        </div>
        : <Form initialBays={settings.bays.sort((a, b) => a.name.localeCompare(b.name))}
          products={settings.products.sort((a, b) => a.name.localeCompare(b.name))} />
    } />
}

interface ProductSwitchProps {
  bay: Bay;
  product: Product;
  warning: ProductWarning | null;
  enabled: boolean;
  toggleProduct: (ev: React.ChangeEvent<HTMLInputElement>, bayId: string, id: string) => void;
}

const ProductSwitch = ({ bay, product, ...props }: ProductSwitchProps) => {
  const enabled = bay.products.find(bayProduct => bayProduct.id === product.id)?.enabled ?? false;
  const disabled = props.warning === ProductWarning.NotAvailable ||
    props.warning === ProductWarning.BlendInBulkBay ||
    props.warning === ProductWarning.BlendInReceivingOnlyBay;

  const style = !disabled && !enabled ? { backgroundColor: '#9fbded' } : {}

  return <td className="pb-0 pt-1">
    <div className="d-flex">
      <div className="form-check form-switch mb-4 flex-shrink-0" id={`cbx-${bay.id}-${product.id}`}>
        <input
          type="checkbox"
          className="form-check-input"
          data-testid={`cbx-${bay.id}-${product.id}`}
          style={style}
          disabled={disabled}
          onChange={(ev) => props.toggleProduct(ev, bay.id, product.id)}
          checked={enabled} />
        {
          props.warning === ProductWarning.BulkInBothBay &&
          <i
            className="bi-info-circle"
            id={`info-${bay.id}-${product.id}`}
            data-testid={`info-${bay.id}-${product.id}`} />
        }
      </div>
    </div>
    {
      props.warning &&
      <UncontrolledTooltip placement="top" target={`cbx-${bay.id}-${product.id}`}>
        {props.warning === ProductWarning.BlendInBulkBay &&
          `${product.name} is a blend and cannot be dispensed in a Bulk bay`}

        {props.warning === ProductWarning.NotAvailable &&
          `${product.name} is not configured to be dispensed at this bay`}

        {props.warning === ProductWarning.BulkInBothBay &&
          `Bulk loads containing ${product.name} will not use this bay due to higher priority Bulk bays`}

        {props.warning === ProductWarning.BlendInReceivingOnlyBay &&
          `${product.name} is a blend and cannot be received`}
      </UncontrolledTooltip>
    }
  </td>
}

const disableBlendsInBulkBay = (bay: Bay) => {
  bay.products.forEach(p => {

    if (p.mustBlend) {
      if (bay.dispenseType === DispenseType.Bulk) {
        p.enabled = false;
      }

      if (bay.dispenseType === DispenseType.None && bay.bulkReceiving) {
        p.enabled = false;
      }
    }
  });

  return bay;
}

interface FormProps {
  initialBays: Bay[],
  products: Product[],
}

const Form = (props: FormProps) => {
  const displayMessage = useDisplayMessage();
  const [bays, setBays] = useState<Bay[]>(props.initialBays.map(b => disableBlendsInBulkBay(b)));
  const [selectedBay, setSelecedBay] = useState<Bay | null>(null);
  const { refreshUser } = useUser();

  const updateBaySettings = useUpdateBaySettings({
    onSuccess: () => {
      displayMessage.success("Bay settings updated");
      refreshUser();
    },
    onError: err => displayMessage.fail(adminFacingErrorMessage(err))
  });

  const toggleProduct = (ev: React.ChangeEvent<HTMLInputElement>, bayId: string, id: string) => {
    const updatedBays: Bay[] = JSON.parse(JSON.stringify(bays));

    const bayIndex = updatedBays.findIndex(b => b.id === bayId);
    const productIndex = updatedBays[bayIndex].products.findIndex(p => p.id === id);

    if (productIndex !== -1) {
      updatedBays[bayIndex].products[productIndex].enabled = ev.target.checked;
      setBays(updatedBays);
    }
  }

  const changeBayModalConfigurations = (
    bay: Bay,
    dispenseType: DispenseType,
    bulkReceiving: boolean,
    temporarilyDisabled: boolean) => {
    const updatedBays: Bay[] = JSON.parse(JSON.stringify(bays));
    const bayIndex = updatedBays.findIndex(b => b.id === bay.id);
    updatedBays[bayIndex].dispenseType = dispenseType;

    if (dispenseType === DispenseType.Bulk) {
      updatedBays[bayIndex] = disableBlendsInBulkBay(updatedBays[bayIndex]);
    }

    updatedBays[bayIndex].bulkReceiving = bulkReceiving;
    updatedBays[bayIndex].temporarilyDisabled = temporarilyDisabled;
    setBays(updatedBays);
    setSelecedBay(null);
  }

  const bulkWarning = (bay: Bay, product: Product) => {
    if (bay.dispenseType === DispenseType.Both && bay.products.some(p => p.id === product.id && p.enabled)) {
      return bays
        .filter(b => b.dispenseType === DispenseType.Bulk)
        .some(b => b.products.some(p => p.id === product.id && p.enabled))
    }

    return false;
  }

  const productWarning = (bay: Bay, product: Product): ProductWarning | null => {
    const productSetting = bay.products.find(bayProduct => bayProduct.id === product.id);

    if (productSetting === undefined) {
      return ProductWarning.NotAvailable;
    }

    if (productSetting.mustBlend) {
      if (bay.dispenseType === DispenseType.Bulk) {
        return ProductWarning.BlendInBulkBay;
      }

      if (bay.dispenseType === DispenseType.None && bay.bulkReceiving) {
        return ProductWarning.BlendInReceivingOnlyBay;
      }
    }

    if (bulkWarning(bay, product)) {
      return ProductWarning.BulkInBothBay;
    }

    return null;
  }

  const save = () => {
    const bayUpdates = bays.map<BayUpdate>(b => ({
      id: b.id,
      dispenseType: b.dispenseType,
      bulkReceiving: b.bulkReceiving,
      temporarilyDisabled: b.temporarilyDisabled,
      products: b.products.map<ProductUpdate>(p => ({ id: p.id, enabled: p.enabled }))
    }));

    updateBaySettings.request(bayUpdates);
  }

  return <>
    {
      selectedBay &&
      <UpdateBayModal
        bay={selectedBay}
        onUpdate={(dispenseType, bulkReceiving, temporarilyDisabled) =>
          changeBayModalConfigurations(selectedBay, dispenseType, bulkReceiving, temporarilyDisabled)}
        toggle={() => setSelecedBay(null)} />
    }
    <div className="px-3">
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
    </div>
    <div className="px-3 mb-3">
      <h2>Bay Directions</h2>
      <p>
        The Bay Directions page allow users to setup which products are available at each bay, ensuring accurate product placement.
        This information is then used in the LOADPASS app to provide drivers with precise bay directions and loading instructions. Bays are assigned
        when the driver scans their code after arriving at the site.
      </p>
    </div>
    <div className="px-3">
      <div className="p-1 border border-dark rounded" style={{ minWidth: "fit-content" }}>
        <Table borderless>
              <thead>
                <tr className="ka-sticky-top">
                  <th className="ka-sticky-left" />
                  {bays.map(b => <th key={b.id} className="bg-white pb-1">
                    <div style={{ minWidth: "175px" }} >
                      <OverflowTooltip text={b.name} className="h4" />
                    </div>
                    <Row className="flex-nowrap">
                      <p className="col-auto mb-0 pe-1">({dispenseTypeDisplay(b)})</p>
                      <div className="col text-start align-self-end ms-0 p-0 mt-n1">
                        <button className="btn p-1 m-0 d-inline-block" onClick={() => setSelecedBay(b)} >
                          <i className="bi-pencil-square" data-testid={`btn-edit-${b.id}`} />
                        </button>
                      </div>
                    </Row>
                  </th>)}
                </tr>
              </thead>
              <tbody>
                {
                  props.products.map(p =>
                    <tr key={p.id}>
                      <td className="pt-1 d-inline-block ka-sticky-left" style={{ width: "175px" }}>
                        <p className="text-truncate">{p.name}</p>
                      </td>
                      {bays.map(b =>
                        <ProductSwitch
                          key={b.id}
                          bay={b}
                          product={p}
                          enabled={b.products.find(bayProduct => bayProduct.id === p.id)?.enabled ?? false}
                          warning={productWarning(b, p)}
                          toggleProduct={toggleProduct} />
                      )}
                    </tr>
                  )
                }
          </tbody>
        </Table>
      </div>
    </div>
    <div className="align-self-end px-3 mt-3">
      <SpinnerButton
        className="btn btn-primary mb-4 px-5 py-3"
        spinning={updateBaySettings.isLoading}
        onClick={save}>
        Save
      </SpinnerButton>
    </div>
  </>
}

export { BayDirections };
